/**
 * Single source of truth for `?tab=` ids of every tab hub (FEAT-030 ia-map 4.1).
 *
 * Founder decision 01/10/2026: frontend routes are English, kebab-case, ASCII,
 * for paths AND tab ids. Visible text comes from i18n, never from the id.
 *
 * PURE module (no React): it is also imported by `scripts/audit-routes.ts`
 * (STALE TAB ID check). This is the ONLY production file that may contain the
 * legacy pt-BR ids, and only as redirect aliases (LEGACY_TAB_ALIASES) or as the
 * moved-tab list (MOVED_TAB_IDS).
 */

/** Current tab ids per hub, in display order (ia-map 4.1.1). */
export const TAB_IDS = {
  reports: ['overview', 'builder'],
  compliance: ['overview', 'queue', 'intelligence', 'rules'],
  people: ['users', 'teams', 'invitations'],
  models: ['catalog', 'access'],
  orgSettings: ['general', 'security', 'customization', 'ai'],
  classifier: ['presets', 'sandbox', 'dashboard', 'taxonomy'],
  apiKeys: ['keys', 'policies'],
  endpoints: ['catalog', 'how-to-connect', 'test'],
  providers: ['providers', 'resilience', 'events'],
  accountSettings: ['profile', 'security', 'api-key', 'usage'],
  fleet: ['workspaces', 'framework', 'access-quota'],
  quotas: ['tokens', 'budgets', 'pools', 'effective'],
  quotaPools: ['pools', 'wizard', 'consumption'],
  // --- FEAT-038 TASK-010 (tab-ids) ---
  costs: ['overview', 'pricing', 'budgets'],
  // --- fim FEAT-038 TASK-010 (tab-ids) ---
  // --- FEAT-039 TASK-010 (tab-ids) ---
  analytics: ['usage', 'utilization', 'cache', 'compaction', 'providers'],
  // --- fim FEAT-039 TASK-010 (tab-ids) ---
  // --- FEAT-040 TASK-011 (tab-ids) ---
  logs: ['requests', 'upstream', 'console', 'timeline', 'conversations'],
  operations: ['health', 'runtime'],
  // --- fim FEAT-040 TASK-011 (tab-ids) ---
} as const;

export type TabHub = keyof typeof TAB_IDS;

/** Absolute route path of each tab hub. */
export const TAB_HUB_PATHS: Record<TabHub, string> = {
  reports: '/admin/reports',
  compliance: '/admin/compliance',
  people: '/admin/people',
  models: '/admin/models',
  orgSettings: '/admin/settings',
  classifier: '/admin/classifier',
  apiKeys: '/admin/connections/api-keys',
  endpoints: '/admin/connections/endpoints',
  providers: '/admin/connections/providers',
  accountSettings: '/settings',
  fleet: '/admin/fleet',
  quotas: '/admin/quotas',
  quotaPools: '/admin/connections/quota-pools',
  // --- FEAT-038 TASK-010 (tab-ids) ---
  costs: '/admin/costs',
  // --- fim FEAT-038 TASK-010 (tab-ids) ---
  // --- FEAT-039 TASK-010 (tab-ids) ---
  analytics: '/admin/analytics',
  // --- fim FEAT-039 TASK-010 (tab-ids) ---
  // --- FEAT-040 TASK-011 (tab-ids) ---
  logs: '/admin/logs',
  operations: '/admin/operations',
  // --- fim FEAT-040 TASK-011 (tab-ids) ---
};

/**
 * Legacy pt-BR ids, kept ONLY as redirect aliases (ia-map 4.1.2).
 * Opening a hub with one of these replaces the URL with the new id.
 * Fleet has no aliases ('acesso' is NOT an alias of 'access-quota'); the
 * Connections hubs (endpoints, providers) have none either.
 */
export const LEGACY_TAB_ALIASES: Record<TabHub, Readonly<Record<string, string>>> = {
  reports: {
    'visao-geral': 'overview',
    construtor: 'builder',
  },
  compliance: {
    'visao-geral': 'overview',
    fila: 'queue',
    inteligencia: 'intelligence',
    regras: 'rules',
  },
  people: {
    usuarios: 'users',
    equipes: 'teams',
    convites: 'invitations',
  },
  models: {
    catalogo: 'catalog',
    acesso: 'access',
  },
  orgSettings: {
    geral: 'general',
    seguranca: 'security',
    customizacao: 'customization',
    ia: 'ai',
  },
  classifier: {
    taxonomia: 'taxonomy',
  },
  apiKeys: {
    chaves: 'keys',
    politicas: 'policies',
  },
  // FEAT-035 hubs were published in English: no pt-BR id exists, so no alias
  endpoints: {},
  providers: {},
  accountSettings: {
    perfil: 'profile',
    seguranca: 'security',
    'chave-de-api': 'api-key',
    uso: 'usage',
  },
  fleet: {},
  // FEAT-036 hubs: ids never published in a tag or main, so no alias (FEAT-035 rule, ia-map 4.1.2)
  quotas: {},
  quotaPools: {},
  // --- FEAT-038 TASK-010 (tab-ids) ---
  // New hub: no pt-BR id was ever published
  costs: {},
  // --- fim FEAT-038 TASK-010 (tab-ids) ---
  // --- FEAT-039 TASK-010 (tab-ids) ---
  // hub novo: ids nunca publicados em pt-BR
  analytics: {},
  // --- fim FEAT-039 TASK-010 (tab-ids) ---
  // --- FEAT-040 TASK-011 (tab-ids) ---
  // New hubs: no pt-BR id was ever published
  logs: {},
  operations: {},
  // --- fim FEAT-040 TASK-011 (tab-ids) ---
};

/**
 * Tab that changed page: values of `?tab=` that the source hub diverts to a
 * destination that is not a tab of the source hub (not an alias). Per hub:
 * models -> /admin/connections/providers?tab=providers (FEAT-035);
 * reports -> /admin/costs?tab=overview (FEAT-038).
 */
export const MOVED_TAB_IDS = {
  models: ['providers', 'provedores'],
  // --- FEAT-038 TASK-010 (tab-ids) ---
  reports: ['costs', 'consumption', 'custos', 'consumo'],
  // --- fim FEAT-038 TASK-010 (tab-ids) ---
} as const;

/** Returns the new id when `raw` is a legacy alias of `hub`, otherwise `raw` itself. */
export function resolveTabAlias(hub: TabHub, raw: string): string {
  const aliases = LEGACY_TAB_ALIASES[hub];
  return (Object.prototype.hasOwnProperty.call(aliases, raw) ? aliases[raw] : undefined) ?? raw;
}
